import React from 'react';
import { Chip } from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
} from '@mui/icons-material';
import {
  ListPageFilterRow,
  ListPageFilterToolbarOutlinedButton,
  ListPageFilterToolbarSearchButton,
} from './listPageFilter.styles';
import ListPageMainSearchField, {
  type ListPageMainSearchFieldProps,
} from './ListPageMainSearchField';

export type ListPagePrimaryFilterRowSearchProps = Omit<
  ListPageMainSearchFieldProps,
  'placeholder' | 'variant' | 'size'
> &
  Partial<Pick<ListPageMainSearchFieldProps, 'placeholder' | 'containerSx'>>;

export interface ListPagePrimaryFilterRowProps {
  showMoreFilters: boolean;
  onToggleMoreFilters: () => void;
  onReset: () => void;
  onSearch: () => void;
  moreFiltersActiveCount?: number;
  /** Slot entre Reiniciar y Buscar (p. ej. alcance de sucursal). */
  betweenResetAndSearch?: React.ReactNode;
  trailingActions?: React.ReactNode;
  searchFieldProps: ListPagePrimaryFilterRowSearchProps;
  secondarySearchFieldProps?: ListPagePrimaryFilterRowSearchProps;
}

/**
 * Fila principal unificada (referencia catálogo): buscador → Más → Reiniciar → [scope] → Buscar → trailingActions.
 */
const ListPagePrimaryFilterRow: React.FC<ListPagePrimaryFilterRowProps> = ({
  showMoreFilters,
  onToggleMoreFilters,
  onReset,
  onSearch,
  moreFiltersActiveCount = 0,
  betweenResetAndSearch,
  trailingActions,
  searchFieldProps,
  secondarySearchFieldProps,
}) => {
  const { placeholder = '', ...restSearch } = searchFieldProps;

  return (
    <ListPageFilterRow sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
      <ListPageMainSearchField placeholder={placeholder} {...restSearch} />
      {secondarySearchFieldProps ? (
        <ListPageMainSearchField
          {...secondarySearchFieldProps}
          placeholder={secondarySearchFieldProps.placeholder ?? ''}
        />
      ) : null}

      <ListPageFilterToolbarOutlinedButton
        variant="outlined"
        size="medium"
        onClick={onToggleMoreFilters}
        startIcon={showMoreFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      >
        {showMoreFilters ? 'Menos' : 'Más'}
        {moreFiltersActiveCount > 0 && (
          <Chip
            label={moreFiltersActiveCount}
            size="small"
            sx={{ ml: 1, backgroundColor: 'success.main', color: 'white' }}
          />
        )}
      </ListPageFilterToolbarOutlinedButton>

      <ListPageFilterToolbarOutlinedButton
        variant="outlined"
        size="medium"
        onClick={onReset}
        startIcon={<RefreshIcon />}
      >
        Reiniciar
      </ListPageFilterToolbarOutlinedButton>

      {betweenResetAndSearch}

      <ListPageFilterToolbarSearchButton
        variant="contained"
        size="medium"
        onClick={onSearch}
        startIcon={<SearchIcon />}
      >
        Buscar
      </ListPageFilterToolbarSearchButton>

      {trailingActions}
    </ListPageFilterRow>
  );
};

export default ListPagePrimaryFilterRow;
